import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type UseConfirmModalProps = {
  title: string;
  content: string;
};

type UseConfirmModalReturn = {
  confirm: (callback: () => void) => void;
  ConfirmationModal: (props: UseConfirmModalProps) => ReactNode;
};

export const useModal = (
  isOpenDefault?: boolean
): {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  confirm: (callback: () => void) => void;
  onConfirm: (() => void) | null;
} => {
  const [isOpen, setIsOpen] = useState<boolean>(!!isOpenDefault);
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen((prev) => !prev);

  const confirm = (callback: () => void): void => {
    setOnConfirm(() => callback);
    open();
  };

  return {
    isOpen,
    open,
    close,
    toggle,
    confirm,
    onConfirm,
  };
};

export const useConfirmModal = (): UseConfirmModalReturn => {
  const { t } = useTranslation();

  const { isOpen, close, confirm, onConfirm } = useModal();

  const handleConfirm = (): void => {
    if (onConfirm) onConfirm();
    close();
  };

  const ConfirmationModal = ({ title, content }: UseConfirmModalProps) => (
    <Dialog open={isOpen} onClose={close}>
      <DialogTitle>
        {title ||
          t('ConfirmationModal.Title', {
            defaultValue: 'Confirmation Modal Title',
          })}
      </DialogTitle>
      <DialogContent>
        {content ||
          t('ConfirmationModal.Content', {
            defaultValue: 'Are you sure to complete this action?',
          })}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} color="primary" autoFocus>
          {t('common.cancel', { defaultValue: ' Cancel' })}
        </Button>
        <Button onClick={handleConfirm} color="error">
          {t('common.confirm', { defaultValue: 'Confirm' })}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { confirm, ConfirmationModal };
};
